import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Megaphone, Send, Smartphone, AlertTriangle, Lightbulb, CheckCircle, History, Users, UserCheck, User } from 'lucide-react';
import './NotificationCenter.css';

function NotificationCenter() {
  const [tab, setTab] = useState('envoyer'); // 'envoyer' | 'historique'

  // Formulaire d'envoi
  const [notificationData, setNotificationData] = useState({
    title: '',
    message: '',
    url: '',
    requireInteraction: false
  });
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  // Sélection des destinataires : 'all' | 'role:admin' | 'role:sous_admin' | 'role:agent' | 'users'
  const [recipientMode, setRecipientMode] = useState('all');
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [users, setUsers] = useState([]);

  // Historique
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  // Charger la liste des utilisateurs (pour sélecteur "users")
  useEffect(() => {
    if (tab !== 'envoyer') return;
    axios.get('/api/admin/users-list', auth())
      .then(r => setUsers(Array.isArray(r.data) ? r.data : []))
      .catch(e => console.error('users-list:', e));
  }, [tab]);

  // Charger l'historique
  useEffect(() => {
    if (tab !== 'historique') return;
    setLoadingHistory(true);
    axios.get('/api/admin/notifications-history', auth())
      .then(r => setHistory(Array.isArray(r.data) ? r.data : []))
      .catch(e => console.error('history:', e))
      .finally(() => setLoadingHistory(false));
  }, [tab]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setNotificationData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const toggleUser = (id) => {
    setSelectedUserIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const buildRecipientsPayload = () => {
    if (recipientMode === 'all') return 'all';
    if (recipientMode.startsWith('role:')) return recipientMode;
    if (recipientMode === 'users') return { userIds: selectedUserIds };
    return 'all';
  };

  const recipientLabel = () => {
    if (recipientMode === 'all') return 'Tous les utilisateurs';
    if (recipientMode === 'role:admin') return 'Administrateurs';
    if (recipientMode === 'role:sous_admin') return 'Sous-admins';
    if (recipientMode === 'role:agent') return 'Agents';
    if (recipientMode === 'users') return `${selectedUserIds.length} utilisateur(s) sélectionné(s)`;
    return recipientMode;
  };

  const handleSendNotification = async (e) => {
    e.preventDefault();
    if (!notificationData.title.trim() || !notificationData.message.trim()) {
      alert('Veuillez remplir le titre et le message');
      return;
    }
    if (recipientMode === 'users' && selectedUserIds.length === 0) {
      alert('Veuillez sélectionner au moins un destinataire');
      return;
    }
    setSending(true);
    setResult(null);
    try {
      const payload = { ...notificationData, recipients: buildRecipientsPayload() };
      const response = await axios.post('/api/admin/send-notification', payload, auth());
      setResult({
        success: true,
        message: `Notification envoyée à ${response.data.recipientsCount ?? '?'} destinataire(s) — type : ${response.data.sentTo}`
      });
      setNotificationData({ title: '', message: '', url: '', requireInteraction: false });
      setSelectedUserIds([]);
    } catch (error) {
      console.error('Erreur lors de l\'envoi de la notification:', error);
      setResult({ success: false, message: `Erreur : ${error.response?.data?.error || error.message}` });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="notification-center">
      <div className="notification-center-header">
        <h2><Megaphone size={20} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 6 }} /> Centre de Notifications</h2>
        <p>Envoyez des notifications ciblées à des utilisateurs spécifiques, par rôle ou à tout le monde.</p>
      </div>

      {/* Sous-onglets */}
      <div style={{ display: 'flex', gap: 10, borderBottom: '1px solid #e5e5e5', marginBottom: 24 }}>
        <button
          onClick={() => setTab('envoyer')}
          style={{
            padding: '10px 18px', background: 'transparent', border: 'none',
            borderBottom: tab === 'envoyer' ? '2px solid #667eea' : '2px solid transparent',
            color: tab === 'envoyer' ? '#667eea' : '#666',
            fontWeight: tab === 'envoyer' ? 600 : 400, cursor: 'pointer', fontSize: 14
          }}
        >
          <Send size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} /> Envoyer
        </button>
        <button
          onClick={() => setTab('historique')}
          style={{
            padding: '10px 18px', background: 'transparent', border: 'none',
            borderBottom: tab === 'historique' ? '2px solid #667eea' : '2px solid transparent',
            color: tab === 'historique' ? '#667eea' : '#666',
            fontWeight: tab === 'historique' ? 600 : 400, cursor: 'pointer', fontSize: 14
          }}
        >
          <History size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} /> Historique
        </button>
      </div>

      {tab === 'envoyer' && (
        <div className="notification-form-container">
          <form onSubmit={handleSendNotification} className="notification-form">
            {/* Sélection des destinataires */}
            <div className="form-group">
              <label>Destinataires *</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 6 }}>
                {[
                  { v: 'all', l: 'Tous', icon: <Users size={14} /> },
                  { v: 'role:admin', l: 'Admins', icon: <UserCheck size={14} /> },
                  { v: 'role:sous_admin', l: 'Sous-admins', icon: <UserCheck size={14} /> },
                  { v: 'role:agent', l: 'Agents', icon: <UserCheck size={14} /> },
                  { v: 'users', l: 'Sélectionner...', icon: <User size={14} /> }
                ].map(opt => (
                  <button
                    type="button"
                    key={opt.v}
                    onClick={() => setRecipientMode(opt.v)}
                    style={{
                      padding: '8px 14px',
                      borderRadius: 999,
                      border: recipientMode === opt.v ? '2px solid #667eea' : '1px solid #d0d0d0',
                      background: recipientMode === opt.v ? '#eef0ff' : '#fff',
                      color: recipientMode === opt.v ? '#667eea' : '#444',
                      fontWeight: recipientMode === opt.v ? 600 : 500,
                      fontSize: 13,
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6
                    }}
                  >
                    {opt.icon} {opt.l}
                  </button>
                ))}
              </div>
              <small style={{ display: 'block', marginTop: 8, color: '#666' }}>
                Choix actif : <strong>{recipientLabel()}</strong>
              </small>
            </div>

            {/* Sélecteur d'utilisateurs spécifiques */}
            {recipientMode === 'users' && (
              <div className="form-group">
                <label>Sélectionner les utilisateurs ({selectedUserIds.length})</label>
                <div style={{
                  border: '1px solid #d0d0d0',
                  borderRadius: 8,
                  padding: 8,
                  maxHeight: 220,
                  overflowY: 'auto',
                  background: '#fafafa'
                }}>
                  {users.length === 0 && <div style={{ padding: 8, color: '#999' }}>Chargement...</div>}
                  {users.map(u => (
                    <label
                      key={u.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        padding: '6px 8px',
                        cursor: 'pointer',
                        borderRadius: 4,
                        background: selectedUserIds.includes(u.id) ? '#eef0ff' : 'transparent'
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={selectedUserIds.includes(u.id)}
                        onChange={() => toggleUser(u.id)}
                      />
                      <span style={{ flex: 1, fontSize: 13 }}>{u.nom || u.email}</span>
                      <span style={{ fontSize: 11, color: '#888', textTransform: 'uppercase', letterSpacing: 1 }}>{u.role}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div className="form-group">
              <label htmlFor="title">Titre *</label>
              <input
                type="text" id="title" name="title"
                value={notificationData.title} onChange={handleChange}
                placeholder="Ex: Nouvelle fonctionnalité disponible !"
                maxLength="50" required
              />
              <small>{notificationData.title.length}/50 caractères</small>
            </div>

            <div className="form-group">
              <label htmlFor="message">Message *</label>
              <textarea
                id="message" name="message"
                value={notificationData.message} onChange={handleChange}
                placeholder="Décrivez votre annonce..."
                rows="4" maxLength="200" required
              />
              <small>{notificationData.message.length}/200 caractères</small>
            </div>

            <div className="form-group">
              <label htmlFor="url">URL de destination (optionnel)</label>
              <input
                type="text" id="url" name="url"
                value={notificationData.url} onChange={handleChange}
                placeholder="Ex: /dashboard ou https://example.com"
              />
              <small>Laisser vide pour ouvrir le dashboard</small>
            </div>

            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox" name="requireInteraction"
                  checked={notificationData.requireInteraction} onChange={handleChange}
                />
                <span>Nécessite une action de l'utilisateur (notification persistante)</span>
              </label>
            </div>

            {result && (
              <div className={`notification-result ${result.success ? 'success' : 'error'}`}>
                {result.message}
              </div>
            )}

            <button type="submit" className="btn-send-notification" disabled={sending}>
              {sending ? 'Envoi...' : (
                <><Send size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} /> Envoyer à {recipientLabel().toLowerCase()}</>
              )}
            </button>
          </form>

          <div className="notification-preview">
            <h3><Smartphone size={18} style={{ marginRight: 6, verticalAlign: 'middle' }} /> Aperçu</h3>
            <div className="preview-notification">
              <div className="preview-icon"><img src="/logo-am-stram-gram.png" alt="" /></div>
              <div className="preview-content">
                <div className="preview-title">{notificationData.title || 'Titre de la notification'}</div>
                <div className="preview-message">{notificationData.message || 'Message de la notification...'}</div>
                <div className="preview-meta">
                  <span>E-Shops Pro</span><span>•</span><span>maintenant</span>
                </div>
              </div>
            </div>
            {notificationData.requireInteraction && (
              <p className="preview-note">
                <AlertTriangle size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} /> Notification persistante
              </p>
            )}
          </div>
        </div>
      )}

      {tab === 'historique' && (
        <div style={{ background: '#fff', borderRadius: 12, padding: 20, border: '1px solid #e5e5e5' }}>
          {loadingHistory ? (
            <div style={{ padding: 20, textAlign: 'center', color: '#888' }}>Chargement...</div>
          ) : history.length === 0 ? (
            <div style={{ padding: 20, textAlign: 'center', color: '#888' }}>Aucune notification envoyée pour le moment.</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f5f5f7' }}>
                    <th style={{ padding: 10, textAlign: 'left', fontSize: 12, color: '#666', textTransform: 'uppercase', letterSpacing: 1 }}>Date</th>
                    <th style={{ padding: 10, textAlign: 'left', fontSize: 12, color: '#666', textTransform: 'uppercase', letterSpacing: 1 }}>Titre</th>
                    <th style={{ padding: 10, textAlign: 'left', fontSize: 12, color: '#666', textTransform: 'uppercase', letterSpacing: 1 }}>Destinataires</th>
                    <th style={{ padding: 10, textAlign: 'left', fontSize: 12, color: '#666', textTransform: 'uppercase', letterSpacing: 1 }}>Envoyée par</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map(n => (
                    <tr key={n.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                      <td style={{ padding: 10, fontSize: 12, color: '#666', whiteSpace: 'nowrap' }}>
                        {new Date(n.created_at).toLocaleString('fr-FR')}
                      </td>
                      <td style={{ padding: 10 }}>
                        <div style={{ fontWeight: 600, fontSize: 13 }}>{n.title}</div>
                        <div style={{ fontSize: 12, color: '#777', marginTop: 2 }}>{(n.message || '').slice(0, 100)}{(n.message || '').length > 100 ? '…' : ''}</div>
                      </td>
                      <td style={{ padding: 10, fontSize: 12 }}>
                        {n.recipients_type === 'all' && <span style={{ color: '#667eea', fontWeight: 600 }}>Tous</span>}
                        {n.recipients_type === 'role' && <span>Rôle : <strong>{n.recipients_value}</strong></span>}
                        {n.recipients_type === 'users' && (
                          <span>{(JSON.parse(n.recipients_value || '[]') || []).length} utilisateur(s)</span>
                        )}
                        {!n.recipients_type && <span style={{ color: '#999' }}>—</span>}
                      </td>
                      <td style={{ padding: 10, fontSize: 12, color: '#444' }}>{n.created_by_nom || n.created_by_email || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {tab === 'envoyer' && (
        <div className="notification-tips">
          <h3><Lightbulb size={18} style={{ marginRight: 6, verticalAlign: 'middle' }} /> Bonnes pratiques</h3>
          <ul>
            <li><CheckCircle size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} /> Cible précise (par rôle ou user) → moins de spam</li>
            <li><CheckCircle size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} /> Titres courts (max 50 caractères) et messages clairs</li>
            <li><CheckCircle size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} /> Le mode "Tous" déclenche le segment OneSignal "Total Subscriptions" + WebSocket pour tout le monde</li>
            <li><CheckCircle size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} /> Les utilisateurs reçoivent une push même hors-ligne grâce à OneSignal</li>
          </ul>
        </div>
      )}
    </div>
  );
}

export default NotificationCenter;
